import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { addCalendarDays, weekStartOf } from '@mlm/shared/coaching';
import {
  ErrorNote,
  Eyebrow,
  Loading,
  Row,
  Screen,
  Stat,
  StatStrip,
  statCell,
  TwoCol,
} from '../components/primitives';
import { GoalsPanel } from '../components/panels/goals';
import { ScoreBreakdown } from '../components/panels/score';
import { TodayActionsList } from '../components/panels/today';
import { coaching } from '../lib/api';
import { longDay } from '../lib/format';
import { qk, useActiveGoals, useFeature, useFeatureLabel, useToday } from '../lib/queries';
import { scoreTip } from '../lib/score';
import { useIsDesktop } from '../lib/use-is-desktop';

const days = (n: number) => `${n} ${n === 1 ? 'day' : 'days'}`;

/** SC-32: this week's execution at a glance. Desktop adds goals, today's actions and the score breakdown. */
export function AchievePage() {
  const today = useToday();
  const goals = useActiveGoals();
  const desktop = useIsDesktop();
  const weekStart = today.data ? weekStartOf(today.data.day) : '';
  const weekEnd = weekStart ? addCalendarDays(weekStart, 6) : '';
  const week = useQuery({
    queryKey: qk.actions(weekStart, weekEnd),
    queryFn: () => coaching.actions({ from: weekStart, to: weekEnd }),
    enabled: Boolean(weekStart),
  });

  if (today.isPending) return <Loading />;
  if (today.isError) return <ErrorNote error={today.error} retry={() => void today.refetch()} />;
  const actions = week.data?.actions ?? [];
  const done = actions.filter((a) => a.status === 'done').length;
  const pct = actions.length ? Math.round((done / actions.length) * 100) : 0;
  const todayDone = today.data.actions.filter((a) => a.status === 'done').length;
  const weekLine =
    actions.length === 0
      ? 'Plan your first action for the week.'
      : pct >= 70
        ? `${pct}% completed. You're building momentum.`
        : `${pct}% completed. Every finished action counts.`;

  if (desktop) {
    const { score, streak } = today.data;
    return (
      <Screen wide eyebrow="Achieve" title="Achieve">
        <StatStrip>
          <Stat
            compact
            className={statCell}
            eyebrow="This week"
            value={`${done} of ${actions.length} actions`}
            progress={pct}
            detail={weekLine}
          />
          <Stat
            compact
            className={statCell}
            eyebrow="Success Score"
            value={score.total}
            label={score.label}
            detail={scoreTip(score) ?? "Every part of today's score is complete."}
          />
          <Stat
            compact
            className={statCell}
            eyebrow="Current streak"
            value={days(streak.current)}
            detail={
              // A spent freeze is worth saying out loud: the streak survived a
              // day the member knows they missed, and silence reads as a bug.
              streak.freeze?.usedOn
                ? `A streak freeze covered ${longDay(streak.freeze.usedOn)}`
                : `Longest streak: ${days(streak.longest)}`
            }
          />
        </StatStrip>
        <TwoCol
          left={
            goals.isPending ? (
              <Loading />
            ) : goals.isError ? (
              <ErrorNote error={goals.error} retry={() => void goals.refetch()} />
            ) : (
              <GoalsPanel goals={goals.data} variant="hub" />
            )
          }
          right={
            <>
              <TodayActionsList today={today.data} />
              <ScoreBreakdown score={score} heading="Score breakdown" />
              <p className="flex flex-wrap gap-x-6 gap-y-2 px-5 py-4">
                <Link
                  to="/achieve/score"
                  className="eyebrow underline underline-offset-4 hover:text-ink"
                >
                  Score history and streak
                </Link>
                <Link
                  to="/achieve/mood"
                  className="eyebrow underline underline-offset-4 hover:text-ink"
                >
                  Mood
                </Link>
                <Link
                  to="/review/weekly"
                  className="eyebrow underline underline-offset-4 hover:text-ink"
                >
                  Weekly review
                </Link>
              </p>
            </>
          }
        />
      </Screen>
    );
  }

  return (
    <Screen eyebrow="Achieve" title="Achieve">
      <section className="border-b border-border px-5 py-6">
        <Eyebrow>This week</Eyebrow>
        <p className="mt-2 font-display text-4xl font-semibold text-ink">
          {done} of {actions.length} actions
        </p>
        <div className="mt-4 h-px w-full bg-[rgba(16,32,26,0.14)]">
          <div className="h-px bg-verde" style={{ width: `${pct}%` }} />
        </div>
        <p className="mt-3 text-sm text-ink/70">{weekLine}</p>
      </section>
      <Row title="My goals" meta={`${goals.data?.length ?? 0} active`} to="/achieve/goals" />
      <Row
        title="Today's actions"
        meta={`${todayDone} of ${today.data.actions.length} done`}
        to="/achieve/actions"
      />
      <Row title="Success Score" meta={`${today.data.score.total} today`} to="/achieve/score" />
      <ProgramsRow />
      <Row title="Mood" meta="How your days have felt" to="/achieve/mood" />
      <Row title="Weekly review" meta="Five questions to close your week" to="/review/weekly" />
    </Screen>
  );
}

/** The way into guided programs; hidden when the tenant switched them off, marked when a plan holds them. */
function ProgramsRow() {
  const state = useFeature('programs');
  const plan = useFeatureLabel('programs');
  if (state === 'off') return null;
  return (
    <Row
      title="Guided programs"
      meta={
        state === 'locked' ? `A read and one action a day · ${plan}` : 'A read and one action a day'
      }
      to="/programs"
    />
  );
}
